import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Bot, Plus, Save, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Card, Field } from "@/components/ranger/PageHeader";
import { StatusBadge } from "@/components/ranger/StatusBadge";
import { BTN_PRIMARY, BTN_SECONDARY } from "@/pages/Servers";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const inputCls = "border-[#1E2D4A] bg-[#050811] font-mono";

const BotCard = ({ bot }) => (
  <Card title="Bot & OAuth2" icon={Bot} right={<StatusBadge state={bot.state} testId="discord-bot-status" />}>
    <dl className="space-y-3 text-sm">
      <div><dt className="rg-label">Bot user</dt><dd className="text-slate-300">{bot.bot_user || "—"}</dd></div>
      <div><dt className="rg-label">OAuth2 login</dt><dd><StatusBadge state={bot.oauth_configured ? "ready" : "unconfigured"} label={bot.oauth_configured ? "Configured" : "Not configured"} testId="discord-oauth-status" /></dd></div>
      {bot.error && <div><dt className="rg-label">Error</dt><dd className="font-mono text-xs text-red-400">{bot.error}</dd></div>}
      <div><dt className="rg-label">Guilds joined</dt>
        <dd data-testid="discord-guild-list" className="mt-1 space-y-1">{bot.guilds.length ? bot.guilds.map((g) => <div key={g.id} className="font-mono text-xs text-slate-300">{g.name} · {g.id}</div>) : <span className="text-xs text-slate-500">None — bot is offline or not invited.</span>}</dd>
      </div>
    </dl>
    {bot.state === "unconfigured" && <p className="mt-4 text-xs text-amber-300">Set DISCORD_BOT_TOKEN in backend/.env and restart the backend.</p>}
  </Card>
);

const newKey = () => Math.random().toString(36).slice(2);
const withKeys = (d) => ({ ...d, role_mappings: (d.role_mappings || []).map((m) => ({ ...m, _k: newKey() })) });

const Mappings = ({ rows, setRows, disabled }) => (
  <div className="space-y-2">
    {rows.map((m, i) => (
      <div key={m._k} className="grid grid-cols-[1fr_1fr_120px_auto] gap-2">
        <Input data-testid={`role-map-id-${i}`} disabled={disabled} placeholder="Role ID" value={m.role_id} onChange={(e) => setRows(rows.map((r, j) => (j === i ? { ...r, role_id: e.target.value } : r)))} className={inputCls} />
        <Input data-testid={`role-map-name-${i}`} disabled={disabled} placeholder="Label" value={m.role_name} onChange={(e) => setRows(rows.map((r, j) => (j === i ? { ...r, role_name: e.target.value } : r)))} className={inputCls} />
        <Select disabled={disabled} value={m.ranger_role} onValueChange={(v) => setRows(rows.map((r, j) => (j === i ? { ...r, ranger_role: v } : r)))}>
          <SelectTrigger data-testid={`role-map-role-${i}`} className={inputCls}><SelectValue /></SelectTrigger>
          <SelectContent className="border-[#1E2D4A] bg-[#0B1120]">{["viewer", "moderator", "admin"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <button type="button" data-testid={`role-map-remove-${i}`} disabled={disabled} onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Remove mapping" className="px-2 text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
      </div>
    ))}
    {!disabled && <button type="button" data-testid="role-map-add" onClick={() => setRows([...rows, { _k: newKey(), role_id: "", role_name: "", ranger_role: "viewer" }])} className="flex items-center gap-1 text-xs text-[#E5202E] hover:underline"><Plus className="h-3 w-3" />Add role mapping</button>}
  </div>
);

const ChannelPicker = ({ cid, value, onChange, disabled, guildSet }) => {
  const [chans, setChans] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!guildSet || disabled) return;
    api.get(`/communities/${cid}/discord/channels`).then((r) => setChans(r.data)).catch((e) => setErr(errMsg(e)));
  }, [cid, guildSet, disabled]);
  if (chans && chans.length) {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid="discord-channel-select" className={inputCls}><SelectValue placeholder="Choose a staff channel" /></SelectTrigger>
        <SelectContent className="border-[#1E2D4A] bg-[#0B1120]">{chans.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`discord-channel-option-${c.id}`}>#{c.name}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  return (
    <div className="space-y-1">
      <Input data-testid="discord-channel-id-input" disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="123456789012345678" className={inputCls} />
      {err && <span data-testid="discord-channel-error" className="block text-xs text-amber-400">{err}</span>}
    </div>
  );
};

export default function DiscordPage() {
  const { active, can } = useAuth();
  const [bot, setBot] = useState(null);
  const [cfg, setCfg] = useState(null);
  const admin = can("admin");

  const load = useCallback(() => {
    api.get("/discord/status").then((r) => setBot(r.data));
    api.get(`/communities/${active.id}/discord`).then((r) => setCfg(withKeys(r.data)));
  }, [active.id]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/communities/${active.id}/discord`, { guild_id: cfg.guild_id || null, notify_channel_id: cfg.notify_channel_id || null, linked_role_id: cfg.linked_role_id || null, role_mappings: cfg.role_mappings.map(({ _k, ...m }) => m) });
      toast.success("Discord configuration saved");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const testAlert = async () => {
    try { await api.post(`/communities/${active.id}/discord/test-alert`); toast.success("Test alert delivered"); } catch (err) { toast.error(errMsg(err)); }
  };

  if (!bot || !cfg) return null;
  return (
    <div>
      <PageHeader eyebrow="Discord integration" title="Discord" description="Link this community to its Discord guild, choose the alert channel, and map Discord roles to RANGER staff permissions." />
      <div className="grid gap-5 lg:grid-cols-[1fr_2fr]">
        <BotCard bot={bot} />
        <Card title="Community guild">
          <form onSubmit={save} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Guild ID" hint={cfg.guild_id ? (cfg.bot_in_guild ? `Bot is in ${cfg.guild?.name}` : "Bot is not in this guild") : null}>
                <Input data-testid="discord-guild-id-input" disabled={!admin} value={cfg.guild_id || ""} onChange={(e) => setCfg({ ...cfg, guild_id: e.target.value })} placeholder="123456789012345678" className={inputCls} />
              </Field>
              <Field label="Staff alert channel" hint="Gets RCON connects, drops and failures, and new player links.">
                <ChannelPicker cid={active.id} value={cfg.notify_channel_id} onChange={(v) => setCfg({ ...cfg, notify_channel_id: v })} disabled={!admin} guildSet={!!cfg.guild_id && cfg.bot_in_guild} />
              </Field>
            </div>
            <Field label="Linked player role ID (optional)" hint="Given to players after they verify with /link. The bot needs Manage Roles and must sit above this role.">
              <Input data-testid="discord-linked-role-input" disabled={!admin} value={cfg.linked_role_id || ""} onChange={(e) => setCfg({ ...cfg, linked_role_id: e.target.value })} placeholder="123456789012345678" className={inputCls} />
            </Field>
            <div>
              <div className="rg-label mb-2">Role → permission mappings</div>
              <Mappings rows={cfg.role_mappings} setRows={(rows) => setCfg({ ...cfg, role_mappings: rows })} disabled={!admin} />
              <p className="mt-2 text-xs text-slate-500">Checked at each Discord login via the bot. Only the owner can map a role to admin.</p>
            </div>
            {admin && (
              <div className="flex flex-wrap gap-2">
                <button data-testid="discord-save-button" className={BTN_PRIMARY}><Save className="h-4 w-4" />Save</button>
                <button type="button" data-testid="discord-test-alert-button" onClick={testAlert} disabled={!cfg.notify_channel_id} className={BTN_SECONDARY}><BellRing className="h-4 w-4" />Send test alert</button>
              </div>
            )}
          </form>
        </Card>
      </div>
    </div>
  );
}
